import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  Badge,
  Button,
  Card,
  CardBody,
  DataTable,
  Dialog,
  Field,
  Select,
  TextArea,
  TextInput,
  formatDate,
  relativeDue,
} from "@odingard/design-system";
import type {
  EvidenceCollection,
  EvidenceRequest,
  EvidenceRequestStatus,
} from "@odingard/sdk-client";
import { useAuth } from "../../lib/auth";
import { useAsync } from "../../lib/useAsync";
import { api, evidenceApi } from "../../lib/api";
import {
  GOVERNANCE_PERMISSIONS,
  GovPanel,
  GovernedAction,
  useGovernedMutation,
} from "../../governance";
import { RequestStatusBadge, isOverdue } from "./evidenceLabels";

const STATUSES: EvidenceRequestStatus[] = [
  "open",
  "submitted",
  "accepted",
  "rejected",
  "cancelled",
];

interface Member {
  id: string;
  email: string;
  displayName: string;
}

// Evidence requests (APP-004 R3 P2): who owes what evidence, by when.
//
// Accepting and rejecting a request are approver decisions; cancelling is not.
// The backend decides which transitions are legal — closed requests simply stop
// offering them.
export function GovEvidenceRequests() {
  const [params, setParams] = useSearchParams();
  const { activeOrg } = useAuth();
  const orgId = activeOrg?.id ?? "";
  const collectionFilter = params.get("collectionId") ?? "";
  const statusFilter = params.get("status") ?? "";

  const setParam = (key: string, value: string) => {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value);
    else next.delete(key);
    setParams(next);
  };

  const options = useAsync<{ members: Member[]; collections: EvidenceCollection[] }>(async () => {
    const [members, collections] = await Promise.all([
      api.members(orgId),
      evidenceApi.collections(orgId),
    ]);
    return { members: members.members, collections: collections.data.collections };
  }, [orgId]);

  const requests = useAsync(
    async () =>
      (
        await evidenceApi.requests(orgId, {
          ...(collectionFilter ? { collectionId: collectionFilter } : {}),
          ...(statusFilter ? { status: statusFilter as EvidenceRequestStatus } : {}),
        })
      ).data.requests,
    [orgId, collectionFilter, statusFilter],
  );

  const members = options.data?.members ?? [];
  const collections = options.data?.collections ?? [];
  const memberName = (userId: string) => {
    const match = members.find((m) => m.id === userId);
    return match ? match.displayName || match.email : userId;
  };
  const collectionName = (id: string) => collections.find((c) => c.id === id)?.name ?? id;

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Evidence requests</h1>
          <p>
            Requests assign the collection of evidence to a person with a due date.{" "}
            <Link to="/studio/governance/evidence">Back to evidence</Link>
          </p>
        </div>
        <div className="badge-row">
          <CreateRequest
            orgId={orgId}
            collections={collections}
            members={members}
            initialCollectionId={collectionFilter}
            onDone={requests.reload}
          />
        </div>
      </div>

      <Card>
        <CardBody>
          <div className="filter-row">
            <Field label="Collection">
              {(id) => (
                <Select
                  id={id}
                  value={collectionFilter}
                  onChange={(e) => setParam("collectionId", e.target.value)}
                >
                  <option value="">All collections</option>
                  {collections.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                    </option>
                  ))}
                </Select>
              )}
            </Field>
            <Field label="Status">
              {(id) => (
                <Select id={id} value={statusFilter} onChange={(e) => setParam("status", e.target.value)}>
                  <option value="">Any status</option>
                  {STATUSES.map((s) => (
                    <option key={s} value={s}>
                      {s}
                    </option>
                  ))}
                </Select>
              )}
            </Field>
          </div>
        </CardBody>
      </Card>

      <GovPanel
        title="Requests"
        state={requests}
        loadingLabel="Loading evidence requests…"
        isEmpty={(d) => d.length === 0}
        emptyTitle="No evidence requests"
        emptyDescription="Create a request to ask someone for evidence by a given date."
      >
        {(rows) => (
          <DataTable
            caption="Evidence requests"
            columns={[
              {
                key: "title",
                header: "Request",
                render: (row) => (
                  <>
                    <strong>{row.title}</strong>
                    <div className="muted-note">{collectionName(row.collectionId)}</div>
                    {row.instructions ? <div className="muted-note">{row.instructions}</div> : null}
                  </>
                ),
              },
              { key: "assignee", header: "Assigned to", render: (row) => memberName(row.assigneeId) },
              {
                key: "due",
                header: "Due",
                render: (row) => (
                  <>
                    {formatDate(row.dueAt)}
                    <div className="badge-row">
                      {isOverdue(row.dueAt, row.status) ? (
                        <Badge tone="danger">Overdue</Badge>
                      ) : (
                        <span className="muted-note">{relativeDue(row.dueAt)}</span>
                      )}
                    </div>
                  </>
                ),
              },
              {
                key: "status",
                header: "Status",
                render: (row) => <RequestStatusBadge status={row.status} />,
              },
              {
                key: "actions",
                header: "",
                render: (row) => (
                  <div className="badge-row">
                    <RequestDecisions orgId={orgId} request={row} onDone={requests.reload} />
                  </div>
                ),
              },
            ]}
            rows={rows as (EvidenceRequest & { id: string })[]}
          />
        )}
      </GovPanel>
    </>
  );
}

/** Only an open or submitted request can still be decided. */
function RequestDecisions({
  orgId,
  request,
  onDone,
}: {
  orgId: string;
  request: EvidenceRequest;
  onDone: () => void;
}) {
  if (request.status !== "open" && request.status !== "submitted") {
    return <span className="muted-note">closed {formatDate(request.closedAt)}</span>;
  }
  return (
    <>
      <GovernedAction
        permission={GOVERNANCE_PERMISSIONS.evidenceApprove}
        label="Accept"
        title="Accept this request"
        description="Records that the evidence supplied for this request is sufficient."
        consequence="The request closes. Accepting does not approve any individual evidence item: that decision is made on the item."
        confirmLabel="Accept request"
        busyLabel="Accepting…"
        triggerVariant="primary"
        onConfirm={() => evidenceApi.acceptRequest(orgId, request.id)}
        onDone={onDone}
      />
      <GovernedAction
        permission={GOVERNANCE_PERMISSIONS.evidenceApprove}
        label="Reject"
        title="Reject this request"
        description="Records that what was supplied does not satisfy the request."
        consequence="The request closes as rejected. Raise a new request to ask again."
        confirmLabel="Reject request"
        busyLabel="Rejecting…"
        destructive
        onConfirm={() => evidenceApi.rejectRequest(orgId, request.id)}
        onDone={onDone}
      />
      <GovernedAction
        permission={GOVERNANCE_PERMISSIONS.evidenceManage}
        label="Cancel"
        title="Cancel this request"
        description="Withdraws the request without a decision on the evidence."
        consequence="The request closes as cancelled and stops appearing as outstanding work."
        confirmLabel="Cancel request"
        busyLabel="Cancelling…"
        onConfirm={() => evidenceApi.cancelRequest(orgId, request.id)}
        onDone={onDone}
      />
    </>
  );
}

function CreateRequest({
  orgId,
  collections,
  members,
  initialCollectionId,
  onDone,
}: {
  orgId: string;
  collections: EvidenceCollection[];
  members: Member[];
  initialCollectionId: string;
  onDone: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [collectionId, setCollectionId] = useState(initialCollectionId);
  const [title, setTitle] = useState("");
  const [instructions, setInstructions] = useState("");
  const [assigneeId, setAssigneeId] = useState("");
  const [dueAt, setDueAt] = useState("");
  const mutation = useGovernedMutation(GOVERNANCE_PERMISSIONS.evidenceManage);

  const collection = collectionId || collections[0]?.id || "";
  const assignee = assigneeId || members[0]?.id || "";

  const submit = async () => {
    const ok = await mutation.run(() =>
      evidenceApi.createRequest(orgId, collection, {
        title: title.trim(),
        instructions: instructions.trim(),
        assigneeId: assignee,
        // A date input gives a day; the backend stores an instant.
        dueAt: dueAt ? new Date(`${dueAt}T00:00:00.000Z`).toISOString() : null,
      }),
    );
    if (!ok) return;
    setOpen(false);
    setTitle("");
    setInstructions("");
    onDone();
  };

  return (
    <>
      <Button
        variant="primary"
        size="sm"
        disabled={collections.length === 0}
        onClick={() => setOpen(true)}
      >
        New request
      </Button>
      <Dialog
        open={open}
        title="New evidence request"
        description="Asks one person for evidence, with a due date the aging pass can chase."
        onClose={() => setOpen(false)}
        footer={
          <>
            <Button variant="ghost" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button
              variant="primary"
              disabled={title.trim().length === 0 || !collection || !assignee || mutation.busy}
              onClick={() => void submit()}
            >
              {mutation.busy ? "Creating…" : "Create request"}
            </Button>
          </>
        }
      >
        {mutation.error ? (
          <p className="ods-denial" role="alert">
            {mutation.error}
          </p>
        ) : null}
        <Field label="Collection">
          {(id) => (
            <Select id={id} value={collection} onChange={(e) => setCollectionId(e.target.value)}>
              {collections.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </Select>
          )}
        </Field>
        <Field label="Title">
          {(id) => <TextInput id={id} value={title} onChange={(e) => setTitle(e.target.value)} />}
        </Field>
        <Field label="Instructions" help="What to attach, and where it comes from.">
          {(id) => (
            <TextArea
              id={id}
              rows={3}
              value={instructions}
              onChange={(e) => setInstructions(e.target.value)}
            />
          )}
        </Field>
        <Field label="Assign to">
          {(id) => (
            <Select id={id} value={assignee} onChange={(e) => setAssigneeId(e.target.value)}>
              {members.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.displayName || m.email}
                </option>
              ))}
            </Select>
          )}
        </Field>
        <Field label="Due date">
          {(id) => (
            <TextInput id={id} type="date" value={dueAt} onChange={(e) => setDueAt(e.target.value)} />
          )}
        </Field>
      </Dialog>
    </>
  );
}
